import React from "react";
import {
  Grid,
  Box,
  Typography,
  useMediaQuery,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { motion } from "framer-motion";
import { styled, useTheme } from "@mui/material/styles";

import Car from "../../images/png/Car-removebg-preview.png";
import ReportTable from "./ReportTable";
import AccountStatistics from "./AccountStatitics";
import LastReportDetail from "./LastReportDetail";
import { useNavigate } from "react-router-dom";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "90vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    transform: "scale(0.97)",
  },
}));

const Content = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "auto",
  padding: theme.spacing(1),
  marginTop: theme.spacing(0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    maxWidth: "100%",
    marginTop: theme.spacing(-1),
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(1),
  position: "relative",
  transform: "scale(0.97)",
  [theme.breakpoints.down("md")]: {
    transform: "scale(0.95)",
  },
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "2rem",
  fontWeight: 700,
  color: "#4b5b5d",

  [theme.breakpoints.down("md")]: {
    fontSize: "1.8rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.4rem",
  },
}));

const ActionContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    width: "100%",
  },
}));

const ProfitContent = styled(Box)(({ theme }) => ({
  width: "auto",
  maxWidth: "auto",
  padding: theme.spacing(2),
  background: "linear-gradient(to left, #f4efe6, #4b5b5d)",
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  transform: "scale(0.97)",
  [theme.breakpoints.down("md")]: {
    transform: "scale(0.95)",
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontSize: "2.5rem",
  fontWeight: 700,
  fontFamily: '"Libre Baskerville", serif',
  color: "#F5F0E7",
  [theme.breakpoints.down("md")]: {
    fontSize: "2rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.6rem",
  },
}));

const SubTitleText = styled(Typography)(({ theme }) => ({
  fontSize: "1.1rem",
  fontWeight: 500,
  fontFamily: '"Libre Baskerville", serif',
  color: "#F5F0E7",
  [theme.breakpoints.down("md")]: {
    fontSize: "0.9rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
  },
}));

const CarImage = styled("img")(({ theme }) => ({
  maxWidth: "600px",
  height: "auto",
  [theme.breakpoints.down("md")]: {
    maxWidth: "250px",
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100px",
  },
}));

const StartButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(3),
  backgroundColor: "#f4efe6",
  boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
  color: "#4b5b5d",
  fontFamily: '"Libre Baskerville", serif',
  fontSize: "1.1rem",
  fontWeight: 600,
  padding: theme.spacing(0.9, 3),
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#e8e5dc",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.8, 2),
  },
}));

const ExportButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(to top, #758d8e, #4b5b5d)",
  color: "#fff",
  borderRadius: "8px",
  justifyContent: "center",
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1, 3),
  "&:hover": {
    backgroundColor: "#354142",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.8, 2),
    width: "100%",
  },
}));

const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 8,
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
    backgroundColor: "#b5b4b1",
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
    },
  },
  "& .MuiSwitch-switchBase": {
    "&.Mui-checked": {
      color: "#323c3d",
      "& + .MuiSwitch-track": {
        backgroundColor: "#323c3d",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 16,
    height: 16,
    margin: 2,
    backgroundColor: "#ffffff",
  },
}));

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // Retrieve user role from localStorage
  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = userData?.role || "customer";

  return (
    <Container>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Content>
          <HeaderContainer>
            <WelcomeText>Dashboard</WelcomeText>
            <ActionContainer>
              {/* <FormControlLabel
                sx={{
                  backgroundColor: "#E9E9E9",
                  padding: 0.5,
                  borderRadius: 2,
                }}
                control={<Android12Switch />}
                label={
                  <Typography
                    sx={{
                      fontFamily: '"Almarai", san serif',
                      color: "#455354",
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      mr: 1,
                    }}
                  >
                    Estimator Hook
                  </Typography>
                }
              /> */}
              {/* <ExportButton variant="contained">Export Report</ExportButton> */}
            </ActionContainer>
          </HeaderContainer>

          <Grid container spacing={0}>
            <Grid size={{ xs: 12 }}>
              <ProfitContent>
                <Box>
                  <TitleText>Start a New EV Estimation</TitleText>
                  <SubTitleText>
                    Estimate your 5-year net profit based on charger location
                    and setup.
                  </SubTitleText>

                  {userRole === "customer" && (
                    <StartButton
                      variant="contained"
                      onClick={() => {
                        navigate("/main/generate-reports");
                      }}
                    >
                      Start Evaluation
                    </StartButton>
                  )}
                </Box>
                {!isMobile && <CarImage src={Car} alt="Money Tree" />}
              </ProfitContent>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {userRole === "customer" && <LastReportDetail />}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {userRole === "customer" && <AccountStatistics />}
            </Grid>
            <Grid size={{ xs: 12 }}>
              {userRole === "customer" && <ReportTable />}
            </Grid>
          </Grid>
        </Content>
      </motion.div>
    </Container>
  );
};

export default Dashboard;
